/**
 * @module index
 * @package @geenius/adapters/react-native
 * @description Re-exports the public React Native adapter package surface.
 * The provider and UI affordances mirror the React reference contract while
 * rendering through native primitives instead of DOM nodes or CSS.
 */

/** @doc Public React Native loading, error, and toast bridge components. */
export { ErrorBoundaryBridge, LoadingBridge, ToastBridge } from "./bridges";
/** @doc Public React Native adapter status, card, list, and configuration form components. */
export {
  AdapterCard,
  AdapterConfigForm,
  AdapterList,
  AdapterStatusBadge,
} from "./components";
/**
 * Joins truthy class-name fragments for compatibility with shared examples.
 *
 * @param classes Class-name fragments or falsy placeholders.
 * @returns A space-delimited class-name string.
 * @example
 * ```ts
 * const className = cn("base", isActive && "active");
 * ```
 */
export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}
/** @doc Public React hooks for all configured adapter domains and status state. */
export {
  useAdapterStatus,
  useAdapterStatuses,
  useAdapters,
  useAdmin,
  useAi,
  useAuth,
  useAuthAdapter,
  useDb,
  useFileStorage,
  useIsAdapterReady,
  useLogger,
  usePayments,
  useStorage,
} from "./hooks";
/** @doc Public React Native route-ready adapter overview and detail pages. */
export { AdapterDetailPage, AdaptersPage } from "./pages";
/** @doc Public React Native context provider, provider alias, and context access hook. */
export {
  AdapterProvider,
  AdaptersProvider,
  useAdapterContext,
} from "./provider";
/** @doc Public React Native provider, component, bridge, page, and adapter status prop types. */
export type {
  AdapterCardProps,
  AdapterConfigFormProps,
  AdapterContextValue,
  AdapterDetailPageProps,
  AdapterHealthProbeMap,
  AdapterListProps,
  AdapterProviderProps,
  AdapterRegistry,
  AdapterSet,
  AdapterStatusBadgeProps,
  AdaptersPageProps,
  ErrorBoundaryBridgeProps,
  LoadingBridgeProps,
  ReactNativeAsyncStorage,
  ToastBridgeProps,
} from "./types";
